export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES'
  }).format(amount);
};

export const formatDateTime = (dateString: string) => {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

export const formatRelativeTime = (dateString: string) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
};

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatPhoneNumber = (phone: string) => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format as Kenyan phone number
  if (cleaned.length === 10 && cleaned.startsWith('0')) {
    return `+254${cleaned.slice(1)}`;
  } else if (cleaned.length === 12 && cleaned.startsWith('254')) {
    return `+${cleaned}`;
  } else if (cleaned.length === 9) {
    return `+254${cleaned}`;
  }
  
  return phone;
};

/**
 * Format Kenyan phone while the user types — allows 07..., 7..., or 254... without
 * forcing international format mid-entry.
 */
export function formatPhoneKEForInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  if (digits.startsWith('254')) {
    let local = digits.slice(3);
    if (local.startsWith('0')) local = local.slice(1);
    if (local.length > 0 && (local[0] === '7' || local[0] === '1')) {
      return ('0' + local).slice(0, 10);
    }
    return digits.slice(0, 12);
  }

  if (digits.startsWith('0')) {
    return digits.slice(0, 10);
  }

  if (digits.startsWith('7') || digits.startsWith('1')) {
    return digits.slice(0, 9);
  }

  return digits.slice(0, 10);
}

/**
 * Normalize Kenyan phone to 254XXXXXXXXX (digits only) for API/storage.
 * Accepts 07XXXXXXXX, 7XXXXXXXX, +254..., or 254...
 */
export function normalizePhoneKE(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';

  if (digits.startsWith('254')) {
    let local = digits.slice(3);
    if (local.startsWith('0')) local = local.slice(1);
    if (local.length >= 9) return '254' + local.slice(0, 9);
    return '';
  }

  if (digits.startsWith('0') && digits.length >= 10) {
    return '254' + digits.slice(1, 10);
  }

  if (digits.length >= 9 && (digits.startsWith('7') || digits.startsWith('1'))) {
    return '254' + digits.slice(0, 9);
  }

  return '';
}

/** Show stored/API phone as local 07XXXXXXXX when possible. */
export function displayPhoneKE(phone: string): string {
  const normalized = normalizePhoneKE(phone);
  if (/^254\d{9}$/.test(normalized)) {
    return '0' + normalized.slice(3);
  }
  return formatPhoneKEForInput(phone);
}

export function isValidKenyanPhoneKE(phone: string): boolean {
  return /^254\d{9}$/.test(normalizePhoneKE(phone));
} 